import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Trash2, Search, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const STATUS_BADGES = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  saved: 'bg-blue-100 text-blue-700 border-blue-200',
  verified: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

export const GRNSelectiveDeletePanel = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = user?.role_name === 'HQ Super Admin';

  const [grns, setGrns] = useState([]);
  const [selected, setSelected] = useState({}); // {id: {id, grn_type}}
  const [loading, setLoading] = useState(false);

  // Filters
  const [grnType, setGrnType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetKey, setResetKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchGRNs = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setLoading(true);
      const params = {};
      if (grnType !== 'all') params.grn_type = grnType;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/system/reset/grn-list', { params });
      setGrns(res.data?.grns || []);
    } catch (e) {
      toast({
        title: 'Failed to load GRNs',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, grnType, statusFilter, dateFrom, dateTo, search, toast]);

  useEffect(() => {
    fetchGRNs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const toggleGRN = (grn) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[grn.id]) delete next[grn.id];
      else next[grn.id] = { id: grn.id, grn_type: grn.grn_type };
      return next;
    });
  };

  const toggleAllVisible = () => {
    const allSelected = grns.every(g => selected[g.id]);
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      grns.forEach(g => { next[g.id] = { id: g.id, grn_type: g.grn_type }; });
      setSelected(next);
    }
  };

  const clearFilters = () => {
    setGrnType('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  const selectedList = Object.values(selected);
  const selectedTotal = grns
    .filter(g => selected[g.id])
    .reduce((sum, g) => sum + (g.grand_total || 0), 0);

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      const res = await api.post('/system/reset/grn-delete', {
        reset_key: resetKey,
        grns: selectedList
      });
      toast({
        title: 'GRN deletion complete',
        description: `${res.data.aggregate?.grns_deleted || 0} GRN(s) deleted with full stock + ledger reversal.`
      });
      setConfirmOpen(false);
      setResetKey('');
      setSelected({});
      fetchGRNs();
    } catch (e) {
      toast({
        title: 'Deletion failed',
        description: e.response?.data?.detail || 'Please check the reset key and try again',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-red-200" data-testid="grn-selective-delete-panel">
      <CardHeader className="bg-red-50 border-b border-red-100">
        <CardTitle className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" /> Delete Selected GRN Records
        </CardTitle>
        <CardDescription className="text-red-600">
          Permanently deletes chosen GRNs and reverses their stock impact, batch entries and vendor ledger entries.
          Requires the master Reset Key. Cannot be undone.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {!isSuperAdmin && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            Only an HQ Super Admin can delete GRN records.
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-gray-50 rounded-lg border">
          <div className="md:col-span-2">
            <Label className="text-xs text-gray-600">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchGRNs(); }}
                placeholder="GRN No, Invoice No, Vendor"
                className="pl-8 h-9"
                data-testid="grn-reset-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Type</Label>
            <Select value={grnType} onValueChange={setGrnType}>
              <SelectTrigger className="h-9" data-testid="grn-reset-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="international">International</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9" data-testid="grn-reset-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="saved">Saved</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" onClick={fetchGRNs} className="h-9" data-testid="grn-reset-apply-filters">
              <Filter className="w-4 h-4 mr-1" /> Apply
            </Button>
            <Button size="sm" variant="outline" onClick={clearFilters} className="h-9">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Date From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Date To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" />
          </div>
        </div>

        {/* List */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={grns.length > 0 && grns.every(g => selected[g.id])}
                onCheckedChange={toggleAllVisible}
                disabled={!isSuperAdmin || grns.length === 0}
                data-testid="grn-reset-select-all"
              />
              <span className="text-xs text-gray-600">
                {selectedList.length} selected of {grns.length} shown
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={fetchGRNs} data-testid="grn-reset-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-red-500" />
              </div>
            ) : grns.length === 0 ? (
              <div className="py-10 text-center text-gray-500 text-sm">
                No GRN records match the current filters.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-xs text-gray-600">
                    <th className="w-10 px-3 py-2"></th>
                    <th className="px-3 py-2">GRN No</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Vendor</th>
                    <th className="px-3 py-2">Invoice</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {grns.map(g => (
                    <tr
                      key={`${g.grn_type}-${g.id}`}
                      className={`border-b hover:bg-red-50/30 cursor-pointer ${selected[g.id] ? 'bg-red-50/60' : ''}`}
                      onClick={() => isSuperAdmin && toggleGRN(g)}
                      data-testid={`grn-reset-row-${g.grn_number}`}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={!!selected[g.id]}
                          disabled={!isSuperAdmin}
                          onCheckedChange={() => toggleGRN(g)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800">{g.grn_number}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">
                          {g.grn_type === 'international' ? 'INTL' : 'Local'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {g.received_date ? formatDate(g.received_date) : '-'}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{g.vendor_name || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{g.invoice_number || '-'}</td>
                      <td className="px-3 py-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${STATUS_BADGES[g.status] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {g.status || '-'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        ₹{(g.grand_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <p className="text-sm text-gray-600" data-testid="grn-reset-selection-summary">
            <strong>{selectedList.length}</strong> GRN(s) selected &middot; Total value:{' '}
            <strong>₹{selectedTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
          </p>
          <Button
            variant="destructive"
            disabled={!isSuperAdmin || selectedList.length === 0}
            onClick={() => setConfirmOpen(true)}
            data-testid="open-grn-delete-confirm-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete Selected GRNs
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-white" data-testid="grn-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Confirm GRN deletion
            </DialogTitle>
            <DialogDescription>
              You are about to permanently delete <strong>{selectedList.length}</strong> GRN record(s).
              This will also:
            </DialogDescription>
          </DialogHeader>

          <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
            <li>Reverse warehouse batch stock (subtract received quantity)</li>
            <li>Remove associated stock movements and stock entries</li>
            <li>Reverse vendor ledger entries and rebalance vendor payables</li>
            <li>Hard delete the GRN documents (this cannot be undone)</li>
          </ul>

          <div className="space-y-2">
            <Label className="text-gray-700">Enter master Reset Key to confirm</Label>
            <Input
              type="password"
              value={resetKey}
              onChange={(e) => setResetKey(e.target.value)}
              placeholder="Reset Key"
              data-testid="grn-delete-reset-key-input"
              autoComplete="new-password"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!resetKey.trim() || submitting}
              onClick={handleDelete}
              data-testid="grn-delete-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default GRNSelectiveDeletePanel;
